import type { ApolloClient } from "@apollo/client";
import { gql } from "graphql-tag";

// The most generic event possible; minimal top-level keys
export interface geminiEvent {
  metadata: Record<string, string>;
  identity: Record<string, string | null>;
  attributes: Record<string, string | null | Record<string, string | null>>;
  annotations: Record<string, unknown> | null;
}

const sendMutation = gql`
  mutation SendGemini($input: SendGemini) {
    sendGemini(input: $input) {
      success
      error
    }
  }
`;

const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[8|9|aA|bB][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Retrieve the appropriate Gemini deployment stage, based on configuration
 */
function getStage(): string {
  return process.env.VUE_APP_GEMINI_STAGE || "development";
}

/**
 * Naive check for a valid UUIDV4.
 * @param uuid
 */
function isValidUUIDv4(uuid: string): boolean {
  return uuidRegex.test(uuid);
}

/**
 * Generate UUIDV4 courtesy of https://github.com/30-seconds/30-seconds-of-code
 */
function uuid(): string {
  return "10000000-1000-4000-8000-100000000000".replace(
    /[018]/g,
    (c: string) => {
      const parsed = parseInt(c);
      return (
        parsed ^
        (window.crypto.getRandomValues(new Uint8Array(1))[0] &
          (15 >> (parsed / 4)))
      ).toString(16);
    }
  );
}

/**
 * @param val
 */
function emptyToNull(val: string | undefined) {
  if (val === "" || val === undefined) {
    return null;
  }
  return val;
}

/**
 * Generate a cookieId on demand or return existing.
 *
 * Exposed to allow easy access when constructing links to telemetryhub.com
 * that pass th_cid for attribution in URL params.
 * @returns Gemini cookie ID to use for attribution purposes.
 */
function geminiCookie(): string {
  // An incoming cookie parameter from the marketing site takes precedence; it will
  // potentially allow us to associate it with account/user. The marketing site sometimes
  // seems to build links with malformed th_cid params; we guard against that here.
  const urlCookie = new URLSearchParams(window.location.search).get("th_cid");
  if (urlCookie && isValidUUIDv4(urlCookie)) {
    window.localStorage.setItem("th_cookieid", urlCookie);
    return urlCookie;
  }
  // Fall back to localStorage if we didn't get the param
  let cookie = window.localStorage.getItem("th_cookieid");
  if (!(cookie && isValidUUIDv4(cookie))) {
    // Create it and store it otherwise
    cookie = uuid();
    localStorage.setItem("th_cookieid", cookie);
  }
  return cookie;
}

/**
 * @param path - An optional marketing path (e.g. `/plans-pricing`, `/contact`)
 * @returns - A marketing url string with a gemini cookie id in the search parameters
 */
function getMarketingUrl(path = ""): string {
  path = path[0] === "/" ? path : `/${path}`;
  const marketingURL = new URL(`https://telemetryhub.com${path}`);
  marketingURL.searchParams.append("th_cid", geminiCookie());
  return marketingURL.href;
}

/**
 * Send a Gemini event via the GraphQL api.
 * @param gEvent - Gemini event to send from the client.
 * @param client - GraphQL client used in sending the event.
 * @returns GraphQL response to the send mutation.
 */
function send(gEvent: geminiEvent, client: ApolloClient<unknown>) {
  return client.query({
    query: sendMutation,
    variables: {
      input: {
        name: gEvent.metadata.schema_name,
        body: JSON.stringify(gEvent),
      },
    },
    fetchPolicy: "no-cache",
  });
}

export default {
  geminiCookie,
  getMarketingUrl,
  getStage,
  send,
  uuid,
  emptyToNull,
};
